import React, { useEffect, useState, useMemo } from 'react';
import { ProSidebar, Menu, MenuItem } from 'react-pro-sidebar';
import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import clientsIcon from '../icons/users.svg';
import userIcon from '../icons/user.svg';
import purchasesIcon from '../icons/corner-down-left.svg';
import catalogIcon from '../icons/book-open.svg';
import dashboardIcon from '../icons/grid.svg';
import ordersIcon from '../icons/package.svg';
import repoIcon from '../icons/layers.svg';
import chevronUp from '../icons/chevron-upG.svg';
import chevronDown from '../icons/chevron-downG.svg';
import logoGF from '../images/logo.png';
import paymentIcon from '../icons/payment.svg';
import performanceIcon from '../icons/performance.svg';

const Sidebar = () => {
  const location = useLocation();
  const [expandedMenu, setExpandedMenu] = useState(null);
  const [userRole, setUserRole] = useState(null);
  const { t } = useTranslation();

  const handleImageError = (e) => {
    e.target.src = logoGF;
    e.target.style.width = "150px";
    e.target.style.height = "50px";
  };

  const roleBasedModules = {
    ROLE_ADMIN: {
      clients: ['list'],
      sales: ['invoiceList'],
      recouvrement: ['payments'],
      administration: ['users', 'banks', 'paymentTypes'],
    },
  };

  const mainMenuItems = useMemo(() => [
    { path: '/', titleKey: 'dashboard', icon: dashboardIcon, menuKey: null, itemKey: null },
    { path: '/clients', titleKey: 'clients', icon: clientsIcon, menuKey: 'clients', itemKey: 'list' },
    { path: '/invoices', titleKey: 'invoices', icon: ordersIcon, menuKey: 'sales', itemKey: 'invoiceList' },
    { path: '/payments', titleKey: 'payments', icon: paymentIcon, menuKey: 'recouvrement', itemKey: 'payments' },
  ], []);

  const submenus = useMemo(() => [
    {
      title: t(`titles.admin`),
      key: 'administration',
      icon: userIcon,
      items: [
        { title: t(`titles.sb_users`), path: '/users', key: 'users' },
        { title: t(`titles.banks`), path: '/banks', key: 'banks' },
        { title: t(`titles.paymentTypes`), path: '/payment-types', key: 'paymentTypes' },
      ],
    },
  ], [t]);

  useEffect(() => {
    const roleStore = localStorage.getItem('user');
    const role = roleStore ? JSON.parse(roleStore).role : null;
    setUserRole(role);
  }, []);

  useEffect(() => {
    const currentPath = location.pathname;

    const matchingMenu = submenus.find((submenu) =>
      submenu.items.some((item) => currentPath.startsWith(item.path))
    );

    if (matchingMenu) {
      setExpandedMenu(matchingMenu.key);
    }
  }, [location.pathname, submenus]);

  const handleToggle = (menuName) => {
    setExpandedMenu(expandedMenu === menuName ? null : menuName);
  };

  const isActiveSubMenu = (path) => location.pathname.startsWith(path);

  const purpleFilter =
    'brightness(0) saturate(100%) invert(36%) sepia(81%) saturate(4000%) hue-rotate(225deg) brightness(95%) contrast(90%)';
  const defaultFilter = 'none';

  const filteredMainMenuItems = mainMenuItems.filter(
    (item) => item.menuKey == null || roleBasedModules[userRole]?.[item.menuKey]?.includes(item.itemKey)
  );

  const filteredSubmenus = submenus
    .map((submenu) => ({
      ...submenu,
      items: submenu.items.filter(
        (item) => roleBasedModules[userRole]?.[submenu.key]?.includes(item.key)
      ),
    }))
    .filter((submenu) => submenu.items.length > 0);

  const logo = localStorage.getItem('logo') ? localStorage.getItem('logo') : logoGF;

  return (
    <ProSidebar
      className="h-screen overflow-y-auto bg-[#F8F9FC] shadow-lg"
      style={{
        width: '100%',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      {/* Logo Section */}
      <div className="flex items-center justify-center h-[140px]">
        <img src={logo} alt="Company Logo" className="h-10" onError={handleImageError} />
      </div>

      <Menu iconShape="square">
        {/* Main menu items (dashboard-style) */}
        {filteredMainMenuItems.map((item) => {
          const isActive = item.path === '/' ? location.pathname === '/' : location.pathname.startsWith(item.path);
          return (
            <MenuItem
              key={item.path}
              active={isActive}
              onClick={() => setExpandedMenu(null)}
              className={`flex items-center px-4 py-3 ${isActive ? 'bg-primary/15 text-primary' : 'text-[#231E29]'}`}
              style={{
                borderRadius: '8px',
                margin: '0 10px',
                display: 'flex',
                alignItems: 'center',
              }}
            >
              <Link to={item.path} className="w-full h-full flex items-center">
                <span className="flex items-center gap-3">
                  <img
                    src={item.icon}
                    alt=""
                    style={{ filter: isActive ? purpleFilter : defaultFilter }}
                  />
                  <span className={isActive ? 'font-semibold' : 'font-medium'}>{t(`titles.${item.titleKey}`)}</span>
                </span>
              </Link>
            </MenuItem>
          );
        })}

        {/* Expandable submenus */}
        {filteredSubmenus.map((submenu) => (
          <div key={submenu.key} className="flex flex-col w-full">
            {/* Parent Menu */}
            <MenuItem
              onClick={() => handleToggle(submenu.key)}
              className={`px-4 py-3 ${expandedMenu === submenu.key
                ? 'bg-primary/15 text-primary'
                : 'text-[#231E29]'
                }`}
              style={{
                borderRadius: '8px',
                margin: '0 10px',
              }}
            >
              <div className="flex items-center justify-between w-full">
                {/* Title and Icon */}
                <div className="flex items-center gap-3">
                  <img
                    src={submenu.icon}
                    alt="Icon"
                    style={{
                      filter: expandedMenu === submenu.key ? purpleFilter : defaultFilter,
                    }}
                  />
                  <span className={`${expandedMenu === submenu.key ? 'font-semibold' : 'font-medium'}`}>{submenu.title}</span>
                </div>
                {/* Chevron */}
                <div className="ml-auto">
                  <img
                    src={expandedMenu === submenu.key ? chevronUp : chevronDown}
                    alt="Chevron"
                    className="w-5 h-5"
                  />
                </div>
              </div>
            </MenuItem>

            {/* Submenu Items */}
            {expandedMenu === submenu.key && (
              <div className="pl-8 pt-2">
                {submenu.items.map((item) => (
                  <MenuItem
                    key={item.path}
                    active={isActiveSubMenu(item.path)}
                    className={`relative px-4 py-2 ${isActiveSubMenu(item.path)
                      ? 'text-primary bg-transparent before:absolute before:left-0 before:top-0 before:bottom-0 before:w-[4px] before:bg-primary'
                      : 'text-[#231E29] before:absolute before:left-0 before:top-0 before:bottom-0 before:w-[1px] before:bg-gray-300'
                      }`}
                  >
                    <Link to={item.path} className={`${isActiveSubMenu(item.path) ? 'font-semibold' : 'font-medium'} w-full h-full flex items-center hover:text-primary transition-colors`}
                    >{item.title}</Link>
                  </MenuItem>
                ))}
              </div>
            )}
          </div>
        ))}
      </Menu>

    </ProSidebar>
  );
};

export default Sidebar;
